import { useEffect, useState } from "react";
import { UserId } from "../authentication/UserId";
import { publicProfileService } from "../profile/publicProfileService";
import { useObjectUrl } from "./useObjectUrl";

// Resolves a contact's chat-facing display name/avatar (see
// docs/plans/chat-public-profile.md §3.4), given the chat key and the
// contact's "public-profile" Document id already resolved from the chat's
// own metadata (see ContactService.loadChatKey). Never surfaces an error:
// a missing/inaccessible profile - no public-profile yet, or the sharing key
// not filed for us yet - simply resolves to an empty name/avatar, so callers
// fall back to their own display (the contact entry's cached name/address).
export function useContactProfile(
  user: UserId,
  contactUserId: string | undefined,
  publicProfileId: string | undefined,
  chatKey: JsonWebKey | undefined,
  // Set for a group member's profile (ADR 0019 decision 4) - undefined for a
  // 1:1 chat's direct grant, where no header is needed.
  accessPath?: string,
  // Where the profile lives and who we are there, if that is not the chat's own server (F8):
  // `owner` is the contact's id there.
  place?: { origin?: string; owner: string; selfId: string },
): { name?: string; avatarUrl?: string; avatarId?: string; revision?: string } {
  const [profile, setProfile] = useState<{
    name?: string;
    avatarBlob?: Blob;
    avatarId?: string;
    revision?: string;
  }>();
  const avatarUrl = useObjectUrl(profile?.avatarBlob);

  useEffect(() => {
    setProfile(undefined);
    if (!contactUserId || !publicProfileId || !chatKey) {
      return;
    }
    let cancelled = false;
    publicProfileService
      .loadContactProfile(
        user,
        place?.owner ?? contactUserId,
        publicProfileId,
        chatKey,
        accessPath,
        place?.selfId,
        place?.origin,
      )
      .then((loaded) => {
        if (!cancelled) {
          setProfile(loaded);
        }
      })
      .catch((e) => console.error("Failed to load contact profile", e));
    return () => {
      cancelled = true;
    };
  }, [
    user,
    contactUserId,
    publicProfileId,
    chatKey,
    accessPath,
    place?.origin,
    place?.owner,
    place?.selfId,
  ]);

  // Treat a blank/whitespace-only name as "no name" so callers fall back to
  // the cached name/address instead of rendering an empty label.
  const name = profile?.name?.trim() || undefined;
  return {
    name,
    avatarUrl,
    avatarId: profile?.avatarId,
    revision: profile?.revision,
  };
}
